export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'off';

type RuntimeLogMethod = 'debug' | 'info' | 'warn' | 'error';

const levelWeight: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  off: 99
};

const originalConsole = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console)
};

let activeLevel: LogLevel = 'info';
let fallbackContext = 'ExternalWebsite';
let appContextPrefix = 'ExternalWebsite';
let configured = false;

export function configureLogging(level: string | undefined, appContext: string): void {
  activeLevel = normalizeLogLevel(level);
  fallbackContext = appContext;
  appContextPrefix = appContext;

  if (configured) {
    return;
  }

  configured = true;

  console.debug = (...args: unknown[]) => emitConsole('debug', args);
  console.info = (...args: unknown[]) => emitConsole('info', args);
  console.log = (...args: unknown[]) => emitConsole('info', args);
  console.warn = (...args: unknown[]) => emitConsole('warn', args);
  console.error = (...args: unknown[]) => emitConsole('error', args);
}

export function createLogger(context: string): Logger {
  return new Logger(context);
}

export class Logger {
  constructor(private readonly context: string) {}

  debug(message: unknown, ...meta: unknown[]): void {
    write('debug', this.context, message, meta);
  }

  info(message: unknown, ...meta: unknown[]): void {
    write('info', this.context, message, meta);
  }

  warn(message: unknown, ...meta: unknown[]): void {
    write('warn', this.context, message, meta);
  }

  error(message: unknown, ...meta: unknown[]): void {
    write('error', this.context, message, meta);
  }
}

function emitConsole(method: RuntimeLogMethod, args: unknown[]): void {
  const [context, message, rest] = extractContext(args);
  write(method, context, message, rest);
}

function write(method: RuntimeLogMethod, context: string, message: unknown, meta: unknown[]): void {
  if (!isEnabled(method)) {
    return;
  }

  const normalizedContext = buildContext(context);
  const renderedMessage = renderMessage(message);
  const prefixedMessage = `[${normalizedContext}] - ${renderedMessage}`;

  switch (method) {
    case 'debug':
      originalConsole.debug(prefixedMessage, ...meta);
      return;
    case 'info':
      originalConsole.info(prefixedMessage, ...meta);
      return;
    case 'warn':
      originalConsole.warn(prefixedMessage, ...meta);
      return;
    case 'error':
      originalConsole.error(prefixedMessage, ...meta);
  }
}

function buildContext(context: string): string {
  const trimmedContext = context.trim();
  if (trimmedContext.length === 0) {
    return `${appContextPrefix}.App`;
  }

  if (trimmedContext === appContextPrefix || trimmedContext.startsWith(`${appContextPrefix}.`)) {
    return trimmedContext;
  }

  return `${appContextPrefix}.${trimmedContext}`;
}

function isEnabled(method: RuntimeLogMethod): boolean {
  const methodWeight = levelWeight[method];
  return methodWeight >= levelWeight[activeLevel];
}

function extractContext(args: unknown[]): [string, unknown, unknown[]] {
  if (args.length === 0) {
    return [fallbackContext, '', []];
  }

  const first = args[0];
  const rest = args.slice(1);

  if (typeof first !== 'string') {
    return [fallbackContext, first, rest];
  }

  const withDash = first.match(/^\[([^\]]+)]\s*-\s*(.*)$/s);
  if (withDash) {
    return [withDash[1], withDash[2], rest];
  }

  const bracketOnly = first.match(/^\[([^\]]+)]\s*(.*)$/s);
  if (bracketOnly) {
    return [bracketOnly[1], bracketOnly[2], rest];
  }

  return [fallbackContext, first, rest];
}

function renderMessage(message: unknown): string {
  if (typeof message === 'string') {
    return message;
  }

  if (message instanceof Error) {
    return message.message;
  }

  try {
    const serializedMessage = JSON.stringify(message);
    if (serializedMessage !== undefined) {
      return serializedMessage;
    }
  } catch (_error) {
  }

  return String(message);
}

function normalizeLogLevel(level: string | undefined): LogLevel {
  if (!level) {
    return 'info';
  }

  const normalized = level.trim().toLowerCase();
  switch (normalized) {
    case 'debug':
    case 'info':
    case 'warn':
    case 'error':
    case 'off':
      return normalized;
    default:
      return 'info';
  }
}
